// frontend/src/pages/admin/User.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Tag, Popconfirm, Input, Select, Badge, Modal } from 'antd';
import { PlusOutlined, EditOutlined, SearchOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import UserForm from './UserForm';
import api from '../../utils/api';
import './users.css';
import dayjs from 'dayjs';
import { useAppContext } from '../../context/AppContext';
import { useNotificationApi } from '../../context/NotificationContext';

const { Search } = Input;
const { Option } = Select;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterUserType, setFilterUserType] = useState('all');
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);

  const { appSettings } = useAppContext();
  const notificationApi = useNotificationApi();

  const dateFormat = appSettings?.dateFormat || 'DD/MM/YYYY';
  const timeFormat = appSettings?.timeFormat || 'hh:mm A';

  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((key, messageContent) => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const fetchUsers = useCallback(async (triggeredByAction = false) => {
    setLoading(true);
    const loadingKey = 'fetchUsersLoading';
    const hideLoading = showLoading(loadingKey, 'Loading users...');

    try {
        const params = {
            status: filterStatus, // Always send the status filter
            search: searchText,
            ...(filterUserType !== 'all' && { userType: filterUserType }),
        };
      const response = await api.get('/api/users', { params });
      let fetchedUsers = response.data?.data || [];

      if (!Array.isArray(fetchedUsers)) {
        console.warn('User.js: API response for users was not an array or expected object format. Assuming empty array.');
        fetchedUsers = [];
      }
      setUsers(fetchedUsers);
      hideLoading();
      
      if (!triggeredByAction) {
        showSuccess('Users Loaded', 'User list fetched successfully!', 'fetchUsersSuccess');
      }
    } catch (error) {
      hideLoading();
      console.error('Error fetching users:', error.response?.data || error.message, error);
      showError('Failed to Fetch Users', error.response?.data?.message || 'An unexpected error occurred while fetching users.', 'fetchUsersError');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, searchText, filterUserType, showLoading, showSuccess, showError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, dateFormat, timeFormat]);

  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalVisible(true);
  };

  const handleEditUser = (userRecord) => {
    console.log('User.js: handleEditUser triggered. User record received:', userRecord);
    setEditingUser(userRecord);
    setIsModalVisible(true);
  };

  const handleDeactivateUser = async (userId, userFirstName, userLastName) => {
    setDeactivatingId(userId);
    const loadingKey = `userStatusUpdate-${userId}`;
    const hideLoading = showLoading(loadingKey, `Deactivating ${userFirstName} ${userLastName}...`);

    try {
      await api.patch(`/api/users/${userId}/deactivate`);
      hideLoading();
      showSuccess('User Status Updated', `${userFirstName} ${userLastName} was deactivated.`, 'userStatusUpdateSuccess');
      fetchUsers(true);
      return true;
    } catch (error) {
      hideLoading();
      console.error('Deactivation error:', error);

      const apiError = error?.response?.data;
      const rawMessage = apiError?.message || error.message || `Failed to deactivate ${userFirstName} ${userLastName}.`;

      showError('Cannot Deactivate User', rawMessage, `deactivateError-${userId}`);
      fetchUsers(true);
      return false;
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleActivateUser = async (userId, userFirstName, userLastName) => {
    setActivatingId(userId);
    const loadingKey = `userStatusUpdate-${userId}`;
    const hideLoading = showLoading(loadingKey, `Activating ${userFirstName} ${userLastName}...`);

    try {
      await api.patch(`/api/users/${userId}/activate`);
      hideLoading();
      showSuccess('User Status Updated', `${userFirstName} ${userLastName} was activated.`, 'userStatusUpdateSuccess');
      fetchUsers(true);
      return true;
    } catch (error) {
      hideLoading();
      console.error('Error activating user:', error);
      const apiError = error?.response?.data;
      const rawMessage = apiError?.message || error.message || `Failed to activate ${userFirstName} ${userLastName}.`;

      showError('Cannot Activate User', rawMessage, `activateError-${userId}`);
      fetchUsers(true);
      return false;
    } finally {
      setActivatingId(null);
    }
  };

  const columns = [
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
      render: (text, record) => (
        <a onClick={() => handleEditUser(record)}>{text}</a>
      ),
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: (a, b) => (a.lastName || '').localeCompare(b.lastName || ''),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (number) => number || 'N/A',
    },
    {
      title: 'User Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (type) => (
        <Tag color={type === 'internal_staff' ? 'green' : type === 'partner' ? 'purple' : 'geekblue'}>
          {type ? type.replace(/_/g, ' ').toUpperCase() : 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'roleName',
      key: 'roleName',
      sorter: (a, b) => (a.roleName || '').localeCompare(b.roleName || ''),
      render: (roleName) => roleName || 'N/A',
    },
    {
      title: 'Organization',
      dataIndex: 'organizationName',
      key: 'organizationName',
      render: (orgName) => orgName || 'N/A',
      sorter: (a, b) => (a.organizationName || '').localeCompare(b.organizationName || ''),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Badge
          status={isActive ? 'success' : 'error'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => date ? dayjs(date).format(`${dateFormat} ${timeFormat}`) : 'N/A',
      sorter: (a, b) => {
        const dateA = a.createdAt ? dayjs(a.createdAt).valueOf() : 0;
        const dateB = b.createdAt ? dayjs(b.createdAt).valueOf() : 0;
        return dateA - dateB;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
            aria-label={`Edit ${record.firstName} ${record.lastName}`}
          />

          {record.isActive ? (
            <Popconfirm
              title={`Are you sure you want to deactivate ${record.firstName} ${record.lastName}?`}
              onConfirm={() => handleDeactivateUser(record.id, record.firstName, record.lastName)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: deactivatingId === record.id, danger: true }}
            >
              <Button type="link" danger icon={<CloseCircleOutlined />} aria-label={`Deactivate ${record.firstName} ${record.lastName}`} />
            </Popconfirm>
          ) : (
            <Popconfirm
              title={`Are you sure you want to activate ${record.firstName} ${record.lastName}?`}
              onConfirm={() => handleActivateUser(record.id, record.firstName, record.lastName)}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: activatingId === record.id }}
            >
              <Button type="link" icon={<CheckCircleOutlined />} aria-label={`Activate ${record.firstName} ${record.lastName}`} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="users-page">
      <div className="users-header">
        <h2>User Management</h2>
        <div className="users-actions">
          <Space size="middle">
            <Search
              placeholder="Search users..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => fetchUsers(true)}
              style={{ width: 300 }}
            />
            <Select
              defaultValue="all"
              size="large"
              style={{ width: 150 }}
              value={filterUserType}
              onChange={(value) => {
                setFilterUserType(value);
                fetchUsers(true);
              }}
            >
              <Option value="all">All Types</Option>
              <Option value="internal_staff">Internal Staff</Option>
              <Option value="partner">Partner</Option>
              <Option value="customer">Customer</Option>
            </Select>
            <Select
              defaultValue="active"
              size="large"
              style={{ width: 120 }}
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                fetchUsers(true);
              }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="all">All Status (Incl. Deleted)</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUser}
              size="large"
            >
              Add User
            </Button>
          </Space>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: true }}
      />

      <UserForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSuccess={fetchUsers}
        user={editingUser}
      />
    </div>
  );
};

export default Users;